import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { collections, featuredProducts } from '../../mocks/products';

export default function CollectionDetailPage({ collectionId }: { collectionId: string }) {
  const col = collections.find((c) => c.id === collectionId);
  if (!col) {
    return (
      <div className="min-w-[1024px]">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-serif text-2xl text-dark">Collection not found</h1>
            <Link to="/collections" className="text-gold mt-4 block">Back to Collections</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const colProducts = featuredProducts.filter((p) => p.collection === col.name);
  const otherProducts = featuredProducts.filter((p) => p.collection !== col.name).slice(0, 3);

  return (
    <div className="min-w-[1024px]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="relative h-[500px] pt-20">
            <img
              src={col.imageLandscape}
              alt={`${col.name} furniture collection Las Vegas`}
              title={`${col.name} Furniture - Eclectic Home`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/80 via-dark/50 to-dark/20" />
            <div className="absolute inset-0 flex items-end pb-12">
              <div className="max-w-7xl mx-auto px-6 w-full">
                <nav className="flex items-center gap-2 text-sm text-white/40 mb-5">
                  <Link to="/" className="hover:text-gold transition-colors cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/collections" className="hover:text-gold transition-colors cursor-pointer">Collections</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-gold">{col.name}</span>
                </nav>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-px bg-gold" />
                  <span className="text-gold text-xs font-semibold uppercase tracking-[0.2em]">{col.productCount}+ Pieces</span>
                </div>
                <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-3">{col.name}</h1>
                <p className="text-white/60 italic text-lg">{col.tagline}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Collection Content */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-serif text-3xl font-bold text-dark mb-5">
                {col.name} Furniture Near Las Vegas
              </h2>
              <p className="text-dark/60 text-sm leading-relaxed mb-5">
                Our {col.name.toLowerCase()} collection brings together the finest modern furniture pieces 
                from artisan makers and design-forward manufacturers worldwide. Every item is personally 
                selected by our buying team for quality of construction, beauty of design, and longevity of style.
              </p>
              <p className="text-dark/60 text-sm leading-relaxed mb-5">
                {col.description} Whether you're starting from scratch or elevating an existing space, 
                our design consultants are here to help you make choices you'll love for years to come.
              </p>
              <p className="text-dark/60 text-sm leading-relaxed mb-10">
                Visit our Las Vegas showroom to experience each piece in person — see the true quality 
                of materials, test comfort levels, and get expert guidance from our certified design team.
              </p>

              {/* What's Included */}
              <h3 className="font-serif text-xl font-bold text-dark mb-5">What's In This Collection</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
                {col.keywords.map((k) => (
                  <div key={k} className="flex items-center gap-3 bg-cream-DEFAULT rounded-xl p-4 border border-cream-dark">
                    <i className="ri-check-line text-gold" />
                    <span className="text-dark text-sm font-medium capitalize">{k}</span>
                  </div>
                ))}
              </div>

              {/* Featured Products */}
              {colProducts.length > 0 && (
                <>
                  <h3 className="font-serif text-xl font-bold text-dark mb-6">Featured Pieces</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {colProducts.map((p) => (
                      <div key={p.id} className="group bg-cream-DEFAULT rounded-xl overflow-hidden border border-cream-dark hover:-translate-y-1 transition-transform">
                        <div className="h-48 overflow-hidden">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-5">
                          <h4 className="font-serif font-bold text-dark text-base">{p.name}</h4>
                          <p className="text-dark/50 text-xs mt-1 mb-3">{p.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-dark">{p.price}</span>
                            <Link to="/contact" className="text-gold text-xs font-bold uppercase tracking-wider hover:underline cursor-pointer">Inquire</Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div>
              <div className="bg-dark rounded-2xl p-8 mb-6 sticky top-28">
                <h3 className="font-serif text-xl font-bold text-white mb-3">Design Consultation</h3>
                <p className="text-white/50 text-sm mb-6">Let our experts help you build the perfect {col.name.toLowerCase()} space.</p>
                <Link to="/contact" className="block w-full text-center bg-gold text-white py-3 rounded font-bold text-xs uppercase tracking-widest hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap mb-3">
                  Book Free Consult
                </Link>
                <a href="tel:7028509900" className="flex items-center justify-center gap-2 w-full border border-white/20 text-white py-3 rounded font-bold text-xs uppercase tracking-wider hover:border-gold hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill" />
                  (702) 850-9900
                </a>
                <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                  {['Free white-glove delivery', 'Professional assembly', '60-day returns', '10-year warranty', 'Custom orders available'].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <i className="ri-check-line text-gold text-sm flex-shrink-0" />
                      <span className="text-white/60 text-xs">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* You May Also Like */}
        <section className="py-16 bg-cream-mid">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="font-serif text-2xl font-bold text-dark mb-8">Explore More Collections</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherProducts.map((p) => (
                <Link key={p.id} to={p.slug} className="group bg-white rounded-xl overflow-hidden border border-cream-dark hover:-translate-y-1 transition-transform cursor-pointer">
                  <div className="h-44 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="text-gold text-[10px] font-bold uppercase tracking-wider">{p.collection}</span>
                    <h4 className="font-serif font-bold text-dark text-base mt-1 group-hover:text-gold transition-colors">{p.name}</h4>
                    <p className="font-bold text-dark text-sm mt-2">{p.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
